// The session as the SPA sees it: the access scope of the cookie the browser sends. The cookie is
// HttpOnly, so this query is the only way to know who is signed in (proposal 6.1).
import { queryOptions, type QueryClient } from "@tanstack/react-query";

import { getAccessScope, getGetAccessScopeQueryKey } from "./generated/endpoints/identity/identity";
import type { AccessScope, TeamScope } from "./generated/model";

export type { AccessScope, TeamScope };

export const accessScopeKey = getGetAccessScopeQueryKey();

export function accessScopeQuery() {
  return queryOptions({
    queryKey: accessScopeKey,
    queryFn: ({ signal }) => getAccessScope({ signal }),
  });
}

/**
 * Reads the scope again, observed or not, and waits for it: the one way to refresh the scope.
 * The route guard reads the cached scope (`ensureQueryData`), and an invalidation refetches only
 * a query some screen observes, so after any write that can change a team, a brand or a role,
 * call this rather than invalidating `accessScopeKey`.
 */
export async function refreshAccessScope(queryClient: QueryClient): Promise<void> {
  await queryClient.refetchQueries({ queryKey: accessScopeKey, type: "all" });
}

/** The team in the scope with this id, compared case-insensitively as UUIDs. */
export function findTeam(scope: AccessScope, teamId: string): TeamScope | undefined {
  const id = teamId.toLowerCase();
  return scope.teams.find((team) => team.team_id.toLowerCase() === id);
}

export function hasBrand(team: TeamScope, brandId: string): boolean {
  const id = brandId.toLowerCase();
  return team.brand_ids.some((brand) => brand.toLowerCase() === id);
}
